import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Users,
  FileText,
  Clock,
  Heart,
  MessageCircle,
  BarChart3,
  Mail,
  TrendingUp,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel({ token }) {
  const [pending, setPending] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, usersRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/admin/all-users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const pendingData = await pendingRes.json();
      const usersData = await usersRes.json();
      const statsData = await statsRes.json();
      setPending(pendingData.verifications || []);
      setAllUsers(usersData.users || []);
      setStats(statsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (userId, action) => {
    setActionLoading(userId);
    try {
      await fetch(`${API}/admin/${action}/${userId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const statusColor = {
    verified: "#00FF66",
    submitted: "#FFB800",
    pending: "#8A8A93",
    rejected: "#FF2E2E",
  };

  return (
    <div className="min-h-screen bg-[#050505] p-6 md:p-10" data-testid="admin-panel">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="w-5 h-5 text-[#00FF66]" />
          <span
            className="text-xs uppercase tracking-[0.2em] text-[#00FF66]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            Admin Control
          </span>
        </div>
        <h1
          style={{ fontFamily: "'Bodoni Moda', serif" }}
          className="text-4xl sm:text-5xl tracking-tighter text-[#FAFAFA] mb-8"
        >
          Command Center
        </h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-[#1F2027]">
          <button
            data-testid="admin-tab-dashboard"
            onClick={() => setActiveTab("dashboard")}
            className={`px-4 py-3 text-sm border-b-2 transition-colors ${
              activeTab === "dashboard"
                ? "border-[#00FF66] text-[#00FF66]"
                : "border-transparent text-[#8A8A93] hover:text-[#FAFAFA]"
            }`}
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <BarChart3 size={14} className="inline mr-2" />
            Dashboard
          </button>
          <button
            data-testid="admin-tab-pending"
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-3 text-sm border-b-2 transition-colors ${
              activeTab === "pending"
                ? "border-[#00FF66] text-[#00FF66]"
                : "border-transparent text-[#8A8A93] hover:text-[#FAFAFA]"
            }`}
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <Clock size={14} className="inline mr-2" />
            Pending ({pending.length})
          </button>
          <button
            data-testid="admin-tab-users"
            onClick={() => setActiveTab("users")}
            className={`px-4 py-3 text-sm border-b-2 transition-colors ${
              activeTab === "users"
                ? "border-[#00FF66] text-[#00FF66]"
                : "border-transparent text-[#8A8A93] hover:text-[#FAFAFA]"
            }`}
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <Users size={14} className="inline mr-2" />
            All Users ({allUsers.length})
          </button>
        </div>

        {loading ? (
          <p
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
            className="text-[#00FF66] text-sm tracking-widest uppercase animate-pulse"
          >
            Loading data...
          </p>
        ) : activeTab === "dashboard" ? (
          /* ===== DASHBOARD TAB ===== */
          <div className="space-y-8 animate-fade-in">
            {/* Admin Info */}
            <div className="flex items-center gap-3 p-4 bg-[#0C0D10] border border-[#1F2027]">
              <Mail className="w-5 h-5 text-[#00FF66]" />
              <div>
                <span className="text-[#8A8A93] text-xs uppercase tracking-widest">Admin Email</span>
                <p className="text-[#FAFAFA] text-sm">{stats?.admin_email}</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#0C0D10] border border-[#1F2027] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-[#00FF66]" />
                  <span className="text-[#8A8A93] text-xs uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Total Users
                  </span>
                </div>
                <p className="text-3xl text-[#FAFAFA] font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                  {stats?.total_users || 0}
                </p>
              </div>

              <div className="bg-[#0C0D10] border border-[#1F2027] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-4 h-4 text-[#00FF66]" />
                  <span className="text-[#8A8A93] text-xs uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Verified
                  </span>
                </div>
                <p className="text-3xl text-[#00FF66] font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                  {stats?.verified_users || 0}
                </p>
              </div>

              <div className="bg-[#0C0D10] border border-[#1F2027] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-[#FFB800]" />
                  <span className="text-[#8A8A93] text-xs uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Pending
                  </span>
                </div>
                <p className="text-3xl text-[#FFB800] font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                  {stats?.pending_users || 0}
                </p>
              </div>

              <div className="bg-[#0C0D10] border border-[#1F2027] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="w-4 h-4 text-[#FF2E2E]" />
                  <span className="text-[#8A8A93] text-xs uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Rejected
                  </span>
                </div>
                <p className="text-3xl text-[#FF2E2E] font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                  {stats?.rejected_users || 0}
                </p>
              </div>
            </div>

            {/* Activity Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#0C0D10] border border-[#1F2027] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-4 h-4 text-[#FF3B6B]" />
                  <span className="text-[#8A8A93] text-xs uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Total Matches
                  </span>
                </div>
                <p className="text-3xl text-[#FAFAFA] font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                  {stats?.total_matches || 0}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <TrendingUp className="w-3 h-3 text-[#00FF66]" />
                  <span className="text-[#8A8A93] text-xs">{stats?.total_likes || 0} likes, {stats?.total_swipes || 0} swipes</span>
                </div>
              </div>

              <div className="bg-[#0C0D10] border border-[#1F2027] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="w-4 h-4 text-[#00AAFF]" />
                  <span className="text-[#8A8A93] text-xs uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Messages Sent
                  </span>
                </div>
                <p className="text-3xl text-[#FAFAFA] font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                  {stats?.total_messages || 0}
                </p>
              </div>

              <div className="bg-[#0C0D10] border border-[#1F2027] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-[#00FF66]" />
                  <span className="text-[#8A8A93] text-xs uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Match Rate
                  </span>
                </div>
                <p className="text-3xl text-[#FAFAFA] font-bold" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                  {stats?.total_likes > 0
                    ? Math.round((stats.total_matches / stats.total_likes) * 100)
                    : 0}%
                </p>
                <span className="text-[#8A8A93] text-xs">of likes become matches</span>
              </div>
            </div>

            {/* Recent Signups */}
            <div>
              <h3 className="text-[#FAFAFA] text-sm font-medium mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#00FF66]" />
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }} className="uppercase tracking-widest text-xs text-[#8A8A93]">
                  Recent Signups
                </span>
              </h3>
              <div className="space-y-2">
                {(stats?.recent_users || []).map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-4 bg-[#0C0D10] border border-[#1F2027]"
                    data-testid={`recent-user-${u.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#14151A] border border-[#1F2027] flex items-center justify-center text-[#8A8A93] text-xs font-bold">
                        {u.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-[#FAFAFA] text-sm">{u.name}</p>
                        <p className="text-[#8A8A93] text-xs">{u.email}</p>
                      </div>
                    </div>
                    <span
                      className="text-xs px-2 py-1 border"
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        color: statusColor[u.verification_status] || "#8A8A93",
                        borderColor: `${statusColor[u.verification_status] || "#8A8A93"}30`,
                        backgroundColor: `${statusColor[u.verification_status] || "#8A8A93"}10`,
                      }}
                    >
                      {u.verification_status?.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : activeTab === "pending" ? (
          /* ===== PENDING TAB ===== */
          <div className="space-y-3">
            {pending.length === 0 ? (
              <div className="text-center py-16">
                <CheckCircle2 className="w-10 h-10 text-[#00FF66] mx-auto mb-3" />
                <p className="text-[#8A8A93] text-sm">
                  No pending verifications
                </p>
              </div>
            ) : (
              pending.map((v) => (
                <div
                  key={v.id}
                  className="bg-[#0C0D10] border border-[#1F2027] p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                  data-testid={`pending-verification-${v.user_id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[#FAFAFA] font-medium text-sm">
                        {v.user_name}
                      </span>
                      <span className="text-[#8A8A93] text-xs">
                        {v.user_email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#8A8A93]">
                      <FileText size={12} />
                      <span
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        REF: {v.fbi_reference}
                      </span>
                    </div>
                    {v.notes && (
                      <p className="text-[#8A8A93] text-xs mt-2">
                        Notes: {v.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`approve-btn-${v.user_id}`}
                      onClick={() => handleAction(v.user_id, "approve")}
                      disabled={actionLoading === v.user_id}
                      className="bg-[#00FF66] text-black font-semibold rounded-none border border-[#00FF66] hover:bg-[#33FF85] h-9 px-4 text-xs uppercase tracking-wider"
                    >
                      <CheckCircle2 size={14} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      data-testid={`reject-btn-${v.user_id}`}
                      onClick={() => handleAction(v.user_id, "reject")}
                      disabled={actionLoading === v.user_id}
                      className="bg-transparent text-[#FF2E2E] border border-[#FF2E2E]/30 rounded-none hover:bg-[#FF2E2E]/10 h-9 px-4 text-xs uppercase tracking-wider"
                    >
                      <XCircle size={14} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* ===== ALL USERS TAB ===== */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1F2027]">
                  <th
                    className="text-left py-3 px-4 text-[#8A8A93] text-xs uppercase tracking-widest"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    User
                  </th>
                  <th
                    className="text-left py-3 px-4 text-[#8A8A93] text-xs uppercase tracking-widest"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    Email
                  </th>
                  <th
                    className="text-left py-3 px-4 text-[#8A8A93] text-xs uppercase tracking-widest"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    Status
                  </th>
                  <th
                    className="text-left py-3 px-4 text-[#8A8A93] text-xs uppercase tracking-widest"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    Role
                  </th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-[#1F2027]/50 hover:bg-white/[0.02] transition-colors"
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="py-3 px-4 text-[#FAFAFA] text-sm">
                      {u.name}
                    </td>
                    <td className="py-3 px-4 text-[#8A8A93] text-sm">
                      {u.email}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className="text-xs px-2 py-1 border"
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          color: statusColor[u.verification_status] || "#8A8A93",
                          borderColor: `${statusColor[u.verification_status] || "#8A8A93"}30`,
                          backgroundColor: `${statusColor[u.verification_status] || "#8A8A93"}10`,
                        }}
                      >
                        {u.verification_status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#8A8A93] text-sm">
                      {u.is_admin ? (
                        <span className="text-[#00FF66]">Admin</span>
                      ) : (
                        "User"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
